import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, PackageCheck, PlayCircle, ScanBarcode } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { formatDate, formatNumber } from "@/lib/format";
import { StatusBadge } from "@/lib/orders";
import { useAuth } from "@/context/AuthContext";

const beep = (ok) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = ok ? "sine" : "square";
    o.frequency.value = ok ? 880 : 180;
    g.gain.value = ok ? 0.08 : 0.35;
    o.connect(g); g.connect(ctx.destination); o.start();
    setTimeout(() => { o.stop(); ctx.close(); }, ok ? 90 : 650);
  } catch (e) { /* audio not available */ }
};

const BANNER = {
  ok: "bg-emerald-600 text-white",
  over: "bg-amber-500 text-white",
  wrong: "bg-rose-600 text-white animate-pulse",
  unknown: "bg-rose-600 text-white animate-pulse",
};

function ScanBox({ mode, onScan }) {
  const ref = useRef(null);
  const [code, setCode] = useState("");
  useEffect(() => { ref.current?.focus(); }, [mode]);
  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    const c = code.trim();
    setCode("");
    await onScan(c);
    ref.current?.focus();
  };
  return (
    <form onSubmit={submit} className="flex gap-2">
      <div className="relative flex-1">
        <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 h-6 w-6 text-slate-400" />
        <Input ref={ref} value={code} onChange={(e) => setCode(e.target.value)} placeholder={mode === "pick" ? "Toplama: barkodu okutun ve Enter" : "Kontrol: ikinci okutma ve Enter"}
          className="h-14 pl-12 text-xl font-mono" data-testid="depo-barcode-input" autoComplete="off" />
      </div>
      <Button type="submit" className="h-14 px-6 text-base bg-slate-900" data-testid="depo-scan-submit">Okut</Button>
    </form>
  );
}

function Lines({ lines, field }) {
  return (
    <table className="erp-table text-[15px]" data-testid="depo-lines">
      <thead><tr><th>Raf (rota sırası)</th><th>SKU</th><th>Ürün</th><th>Barkod</th><th className="text-right">Beklenen</th><th className="text-right">{field === "verified_qty" ? "Kontrol" : "Toplanan"}</th></tr></thead>
      <tbody>{lines.map((l) => {
        const done = l[field] >= l.qty;
        return (
          <tr key={l.product_id} className={done ? "bg-emerald-50" : ""} data-testid={`depo-line-${l.sku}`}>
            <td data-testid={`depo-line-raf-${l.sku}`}><span className="inline-block rounded-md bg-slate-900 text-amber-300 px-3 py-1.5 font-mono text-xl font-extrabold tracking-wider">{l.raf || "—"}</span></td><td className="font-mono text-xs">{l.sku}</td><td>{l.name}</td>
            <td className="font-mono text-xs text-slate-500">{l.barcode}</td>
            <td className="text-right font-mono font-semibold">{formatNumber(l.qty)}</td>
            <td className={`text-right font-mono font-bold ${done ? "text-emerald-700" : "text-slate-900"}`} data-testid={`depo-line-count-${l.sku}`}>
              {done && <CheckCircle2 className="inline h-4 w-4 mr-1" />}{formatNumber(l[field])} / {formatNumber(l.qty)}
            </td>
          </tr>);
      })}</tbody>
    </table>
  );
}

export default function PickScreen({ id, onBack }) {
  const [o, setO] = useState(null);
  const [last, setLast] = useState(null);
  const [koli, setKoli] = useState("");
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const { can } = useAuth();
  useEffect(() => { api.get(`/depo/orders/${id}`).then(({ data }) => { setO(data); setKoli(String(Math.max(1, Math.ceil(data.lines.reduce((s, l) => s + (l.koli || 0), 0))))); }); }, [id]);
  const act = async (path, body, msg) => {
    setBusy(true);
    try { const { data } = await api.post(`/depo/orders/${id}/${path}`, body); setO(data); setLast(null); toast.success(msg); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const scan = async (barcode) => {
    try {
      const { data } = await api.post(`/depo/orders/${id}/scan`, { barcode });
      setO(data.order); setLast(data); beep(data.result === "ok");
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!o) return <div className="panel h-64 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  const field = o.status === "kontrol" ? "verified_qty" : "picked_qty";
  const allDone = o.lines.every((l) => l[field] >= l.qty);
  return (
    <div className="space-y-4" data-testid="pick-screen">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={onBack} data-testid="depo-back-button">← Kuyruk</Button>
        <div className="font-mono text-xl font-bold" data-testid="pick-order-no">{o.order_no}</div>
        <StatusBadge status={o.status} />
        <div className="text-slate-600">{o.customer_title}</div>
      </div>
      {o.status === "stok_ayrildi" && (
        <Button className="h-16 text-lg bg-blue-600 hover:bg-blue-700 w-full" disabled={busy} onClick={() => act("start", {}, "Toplama başladı")} data-testid="depo-start-button">
          <PlayCircle className="h-6 w-6 mr-2" />Toplamaya Başla
        </Button>
      )}
      {(o.status === "toplaniyor" || o.status === "kontrol") && (
        <>
          <ScanBox mode={o.status === "kontrol" ? "verify" : "pick"} onScan={scan} />
          {last && (
            <div className={`rounded-md px-5 py-4 text-xl font-bold flex items-center gap-3 ${BANNER[last.result]}`} data-testid={`depo-scan-result-${last.result}`}>
              {last.result === "ok" ? <CheckCircle2 className="h-7 w-7" /> : <AlertTriangle className="h-7 w-7" />}
              <span data-testid="depo-scan-message">{last.result === "wrong" ? "YANLIŞ ÜRÜN! " : ""}{last.message}{last.result !== "ok" ? " · SAYILMADI" : ""}</span>
            </div>
          )}
        </>
      )}
      {o.status === "paketlendi" && (
        <div className="panel p-4 flex flex-wrap items-end gap-3" data-testid="depo-pack-panel">
          <div className="space-y-1"><div className="text-sm text-slate-600">Koli sayısı</div>
            <Input type="number" min={1} value={koli} onChange={(e) => setKoli(e.target.value)} className="h-14 w-40 text-2xl font-mono" data-testid="depo-koli-input" /></div>
          <Button className="h-14 px-6 text-base bg-emerald-600 hover:bg-emerald-700" disabled={busy || !(Number(koli) >= 1)} onClick={() => act("pack", { package_count: Number(koli) }, "Sevke hazır")} data-testid="depo-pack-button">
            <PackageCheck className="h-5 w-5 mr-2" />Sevke Hazır
          </Button>
        </div>
      )}
      {o.status === "sevke_hazir" && <div className="panel p-6 text-center text-emerald-700 text-lg font-semibold" data-testid="depo-done">Sipariş sevke hazır · {o.package_count} koli</div>}
      <div className="panel overflow-x-auto"><Lines lines={o.lines} field={o.status === "kontrol" || o.status === "paketlendi" ? "verified_qty" : "picked_qty"} /></div>
      {(o.status === "toplaniyor" || o.status === "kontrol") && (
        <Button className="h-14 w-full text-base" disabled={busy || !allDone} onClick={() => act("complete", {}, o.status === "toplaniyor" ? "Toplama tamamlandı, kontrole geçildi" : "Kontrol tamamlandı")} data-testid="depo-complete-button">
          {o.status === "toplaniyor" ? "Toplamayı Tamamla → Kontrol" : "Kontrolü Tamamla → Paketleme"}{!allDone && " (tüm kalemler okutulmalı)"}
        </Button>
      )}
      {(o.status === "toplaniyor" || o.status === "kontrol") && !allDone && can("depo", "approve") && (
        <div className="panel p-3 flex flex-wrap items-center gap-2 border-amber-300 bg-amber-50" data-testid="depo-override-panel">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Yönetici onayı gerekçesi (eksik kalemle geç)" className="flex-1 min-w-[240px]" data-testid="depo-override-reason" />
          <Button variant="outline" className="border-amber-500 text-amber-800" disabled={busy || reason.trim().length < 3}
            onClick={() => act("complete", { force: true, reason }, "Yönetici onayıyla geçildi")} data-testid="depo-override-button">Yönetici Onayıyla Geç</Button>
        </div>
      )}
      {o.scans.length > 0 && (
        <div className="panel overflow-x-auto max-h-56" data-testid="depo-scan-log">
          <table className="erp-table text-[12px]"><thead><tr><th>Zaman</th><th>Adım</th><th>Barkod</th><th>Sonuç</th><th>Kullanıcı</th></tr></thead>
            <tbody>{o.scans.map((x, i) => (
              <tr key={i} className={x.result === "ok" ? "" : "bg-rose-50"}><td className="font-mono">{formatDate(x.at)}</td><td>{x.mode === "pick" ? "Toplama" : "Kontrol"}</td>
                <td className="font-mono">{x.barcode}</td><td>{x.message}</td><td>{x.user_name}</td></tr>))}</tbody></table>
        </div>
      )}
    </div>
  );
}
